import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import './CourseCatalog.css';

function CourseCatalog() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: 'All',
    level: 'All',
    priceRange: 'All'
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BASE_URL}/courses`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch courses');
      }

      const result = await response.json();
      
      if (result.success) {
        // Only show published courses to students
        const publishedCourses = result.data.filter(course => course.is_published);
        setCourses(publishedCourses);
      } else {
        throw new Error('Failed to load courses');
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollCourse = (courseId) => {
    // TODO: Implement enrollment logic
    console.log('Enroll in course:', courseId);
    alert('Enrollment feature coming soon!');
  };

  const handleViewCourse = (courseId) => {
    navigate(`/player?courseId=${courseId}`);
  };

  // Filter courses based on search and filters
  const filteredCourses = courses.filter(course => {
    const matchesSearch = searchQuery === '' || 
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.short_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.category?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = filters.category === 'All' || course.category === filters.category;
    const matchesLevel = filters.level === 'All' || course.level === filters.level;
    
    let matchesPrice = true;
    if (filters.priceRange === 'Free') {
      matchesPrice = course.price === 0;
    } else if (filters.priceRange === 'Paid') {
      matchesPrice = course.price > 0;
    }

    return matchesSearch && matchesCategory && matchesLevel && matchesPrice;
  });

  // Get unique categories and levels
  const categories = ['All', ...new Set(courses.map(c => c.category).filter(Boolean))];
  const levels = ['All', ...new Set(courses.map(c => c.level).filter(Boolean))];

  if (loading) {
    return (
      <div className="catalog-loading">
        <div className="spinner-large"></div>
        <p>Loading courses...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="catalog-error">
        <span className="error-icon">⚠️</span>
        <h3>Failed to Load Courses</h3>
        <p>{error}</p>
        <button className="retry-btn" onClick={fetchCourses}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="course-catalog">
      {/* Search and Filters */}
      <div className="catalog-header">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search courses by title, description, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filters-row">
          <div className="filter-group">
            <label>Category:</label>
            <select 
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="filter-select"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Level:</label>
            <select 
              value={filters.level}
              onChange={(e) => setFilters({...filters, level: e.target.value})}
              className="filter-select"
            >
              {levels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Price:</label>
            <select 
              value={filters.priceRange}
              onChange={(e) => setFilters({...filters, priceRange: e.target.value})}
              className="filter-select"
            >
              <option value="All">All</option>
              <option value="Free">Free</option>
              <option value="Paid">Paid</option>
            </select>
          </div>

          <button className="clear-filters-btn" onClick={() => {
            setFilters({ category: 'All', level: 'All', priceRange: 'All' });
            setSearchQuery('');
          }}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className="results-info">
        <p>Showing {filteredCourses.length} of {courses.length} courses</p>
      </div>

      {/* Courses Grid */}
      {filteredCourses.length === 0 ? (
        <div className="no-courses">
          <span className="no-courses-icon">📚</span>
          <h3>No Courses Found</h3>
          <p>Try adjusting your filters or search query</p>
        </div>
      ) : (
        <div className="catalog-grid">
          {filteredCourses.map((course) => (
            <div key={course.id} className="catalog-course-card">
              <div className="catalog-course-thumbnail">
                {course.thumbnail_url ? (
                  <img src={course.thumbnail_url} alt={course.title} />
                ) : (
                  <div className="catalog-course-placeholder">
                    <span>📚</span>
                  </div>
                )}
                {course.is_featured && (
                  <div className="featured-badge">⭐ Featured</div>
                )}
              </div>
              
              <div className="catalog-course-content">
                <div className="catalog-course-tags">
                  <span className="tag tag-category">{course.category || 'General'}</span>
                  <span className="tag tag-level">{course.level || 'Beginner'}</span>
                </div>

                <h3 className="catalog-course-title">{course.title}</h3>
                
                {course.short_description && (
                  <p className="catalog-course-description">
                    {course.short_description.length > 120 
                      ? `${course.short_description.substring(0, 120)}...` 
                      : course.short_description}
                  </p>
                )}

                <div className="catalog-course-meta">
                  <span className="meta-item">
                    <span className="meta-icon">⏱️</span>
                    {course.duration_hours ? `${course.duration_hours}h` : 'N/A'}
                  </span>
                  <span className="meta-item">
                    <span className="meta-icon">🌐</span>
                    {course.language || 'English'}
                  </span>
                  {course.certificate_available && (
                    <span className="meta-item">
                      <span className="meta-icon">🎓</span>
                      Certificate
                    </span>
                  )}
                </div>

                <div className="catalog-course-footer">
                  <div className="catalog-course-price">
                    {course.price === 0 ? (
                      <span className="price-free">Free</span>
                    ) : (
                      <span className="price-amount">${course.price.toFixed(2)}</span>
                    )}
                  </div>
                  
                  <div className="catalog-course-actions">
                    <button 
                      className="btn-view-course"
                      onClick={() => handleViewCourse(course.id)}
                    >
                      View Details
                    </button>
                    <button 
                      className="btn-enroll"
                      onClick={() => handleEnrollCourse(course.id)}
                    >
                      Enroll Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CourseCatalog;
